<template>
  <div class="standard-page">
    <div class="page-header">
      <h2>标准编号: {{ standardData.standardNumber }}</h2>
      <h3>标准名: {{ standardData.standardName }}</h3>
    </div>

    <div class="page-content">
      <el-card class="box-card">
        <div slot="header" class="clearfix">
          <span>标准信息</span>
        </div>
        <div class="text item">
          <el-form
            ref="formRef"
            :model="formData"
            :rules="rules"
            label-width="120px"
          >
            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="标准名称" prop="standardName">
                  <el-input
                    v-model="formData.standardName"
                    placeholder="名称1"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="标准级别" prop="level">
                  <el-select
                    v-model="formData.level"
                    placeholder="请选择或输入级别"
                    filterable
                    allow-create
                    clearable
                    :loading="levelLoading"
                    @visible-change="handleSelectVisible"
                    @change="handleLevelChange"
                  >
                    <el-option
                      v-for="level in levelOptions"
                      :key="level.value"
                      :label="level.label"
                      :value="level.value"
                    />
                    <!-- 如果当前值不在选项中，也显示为一个选项 -->
                    <el-option
                      v-if="
                        formData.level &&
                        !levelOptions
                          .map((l) => l.value)
                          .includes(formData.level)
                      "
                      :key="formData.level"
                      :label="`${formData.level} (自定义)`"
                      :value="formData.level"
                    />
                  </el-select>
                  <div
                    v-if="
                      formData.level &&
                      !levelOptions.map((l) => l.value).includes(formData.level)
                    "
                    class="custom-level-hint"
                  >
                    <i class="fas fa-info-circle"></i> 当前为自定义级别
                  </div>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="标准编号" prop="standardNumber">
                  <el-input
                    v-model="formData.standardNumber"
                    placeholder="标准号1"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="起草人" prop="draftAuthor">
                  <el-input
                    v-model="formData.draftAuthor"
                    placeholder="起草人1"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="起草单位" prop="draftUnit">
                  <el-input v-model="formData.draftUnit" placeholder="单位1" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="归口单位" prop="responsibleUnit">
                  <el-input
                    v-model="formData.responsibleUnit"
                    placeholder="归口单位1"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="发布日期" prop="publicationDate" required>
                  <el-date-picker
                    v-model="formData.publicationDate"
                    type="date"
                    placeholder="请选择发布日期"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="实施日期" prop="implementationDate">
                  <el-date-picker
                    v-model="formData.implementationDate"
                    type="date"
                    placeholder="请选择实施日期"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="发布部门" prop="departmentName">
                  <el-input
                    v-model="formData.departmentName"
                    placeholder="部门名1"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="规范引用文件" prop="normativeDocuments">
                  <el-input
                    v-model="formData.normativeDocuments"
                    placeholder="请输入规范引用文件"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item label="适用范围">
                  <el-input
                    v-model="formData.scope"
                    placeholder="此处为本标准适用范围"
                    type="textarea"
                    :rows="3"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item label="图片">
                  <MultipleUploadComponent
                    ref="multiUploadRef"
                    v-model="formData.imageList"
                    @change="handleImagesChange"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item label="文件">
                  <FileUploadComponent
                    ref="fileUploadRef"
                    :standardId="formData.id"
                    @update:fileName="formData.fileName = $event"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            <div class="action-buttons">
              <el-button type="primary" @click="handleSave">保存</el-button>
              <el-button @click="handleCancel">取消</el-button>
            </div>
          </el-form>
        </div>
      </el-card>
      <term-page
        :terms-data="termsData.terms || []"
        :standard-id="standardData.id"
        :loading="loading"
        @update="fetchTermsData"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed, onMounted } from "vue";
import { ElMessage } from "element-plus";
import type { FormInstance, UploadFile } from "element-plus";
import { uploadFileWithParams, updateFile } from "@/api/menu";
import {
  addStandard,
  updateStandard,
  listStandardByPage,
  getStandardLevels,
} from "@/api/standard";
import { getTermsByStandardId } from "@/api/term";
import TermPage from "@/page/termPage.vue";
import MultipleUploadComponent from "@/components/MultipleUploadComponent.vue";
import FileUploadComponent from "@/components/FileUploadComponent.vue";

interface StandardForm {
  id: number; // ID
  standardName: string; // 标准名称
  level: string; // 级别
  standardNumber: string; // 标准号
  responsibleUnit: string; // 归口单位
  publicationDate: string; // 发布日期
  implementationDate: string; // 实施日期
  scope?: string; // 适用范围
  departmentName?: string; // 部门名称
  draftAuthor?: string; // 起草人
  draftUnit?: string; // 起草单位
  normativeDocuments?: string; // 规范引用文件
  imageList?: string[]; // 图片列表
  image?: string; // 图片预览URL
  file?: string; // 文件
  fileName?: string; // 文件名称
}

const props = defineProps<{
  standardData: any;
}>();

const loading = ref(false);
const termsData = ref<{
  standardId: string | number;
  terms: any[];
}>({
  standardId: props.standardData?.id || "",
  terms: [],
});
const levelOptions = ref<Array<{ value: string; label: string }>>([]);
const levelLoading = ref(false); // 加载状态
const customLevels = ref<string[]>([]); // 存储用户自定义的级别

const formData = ref<StandardForm>({
  id: 0,
  standardName: "",
  level: "",
  standardNumber: "",
  responsibleUnit: "",
  publicationDate: "",
  implementationDate: "",
  scope: "",
  departmentName: "",
  draftAuthor: "",
  draftUnit: "",
  normativeDocuments: "",
  imageList: props.standardData?.image
    ? props.standardData.image.split(",")
    : [],
  image: "",
  file: props.standardData?.file || "",
  fileName: props.standardData?.fileName || "",
});
const fileDatas = ref({
  standardId: props.standardData?.id,
  fileId: props.standardData?.fileId,
  fileName: "",
  file: "",
});
const emit = defineEmits<{
  update: [data: Partial<StandardForm>];
}>();

const formRef = ref<FormInstance>();
const completeFileUrl = computed(() => {
  if (!formData.value.file) return "";
  return formData.value.file.startsWith("http://") ||
    formData.value.file.startsWith("https://")
    ? formData.value.file
    : `http://${formData.value.file}`;
});

// 表单验证规则
// ...existing code...
const rules = {
  standardName: [
    { required: true, message: "请输入标准名称", trigger: "blur" },
  ],
  level: [{ required: true, message: "请选择标准级别", trigger: "change" }],
  standardNumber: [
    { required: true, message: "请输入标准编号", trigger: "blur" },
  ],
  draftAuthor: [{ required: true, message: "请输入起草人", trigger: "blur" }],
  draftUnit: [{ required: true, message: "请输入起草单位", trigger: "blur" }],
  responsibleUnit: [
    { required: true, message: "请输入归口单位", trigger: "blur" },
  ],
  publicationDate: [
    { required: true, message: "请选择发布日期", trigger: "change" },
  ],
  implementationDate: [
    { required: true, message: "请选择实施日期", trigger: "change" },
  ],
  departmentName: [
    { required: true, message: "请输入发布部门", trigger: "blur" },
  ],
  normativeDocuments: [
    { required: true, message: "请输入规范引用文件", trigger: "blur" },
  ],
};
// ...existing code...
// 加载级别选项的函数
const loadLevelOptions = async () => {
  try {
    levelLoading.value = true;

    // 调用新接口获取级别列表
    const response = await getStandardLevels();

    if (response?.code === 1 && response.data) {
      // 将接口返回的数据直接作为选项
      const levels = response.data;

      // 合并自定义级别
      const allLevels = [...levels];
      customLevels.value.forEach((customLevel) => {
        // 检查自定义级别是否已存在
        if (!allLevels.some((level) => level.value === customLevel)) {
          allLevels.push({ value: customLevel, label: customLevel });
        }
      });

      // 确保有默认选项
      if (allLevels.length === 0) {
        allLevels.push(
          { value: "国家级", label: "国家级" },
          { value: "省级", label: "省级" },
          { value: "市级", label: "市级" },
          { value: "企业级", label: "企业级" }
        );
      }

      levelOptions.value = allLevels;
    }
  } catch (error) {
    console.error("加载级别选项失败:", error);
    // 如果接口失败，使用默认选项并合并自定义级别
    const allLevels = [
      { value: "国家级", label: "国家级" },
      { value: "省级", label: "省级" },
      { value: "市级", label: "市级" },
      { value: "企业级", label: "企业级" },
    ];
    customLevels.value.forEach((customLevel) => {
      if (!allLevels.some((level) => level.value === customLevel)) {
        allLevels.push({ value: customLevel, label: customLevel });
      }
    });
    levelOptions.value = allLevels;
  } finally {
    levelLoading.value = false;
  }
};
// 处理下拉框显示事件
const handleSelectVisible = (visible: boolean) => {
  if (visible) {
    // 每次打开下拉框都重新加载，确保获取最新数据
    loadLevelOptions();
  }
};

// 处理级别变化事件
const handleLevelChange = (value: string) => {
  if (
    value &&
    !levelOptions.value.some((level) => level.value === value) &&
    !customLevels.value.includes(value)
  ) {
    // 如果是新的自定义级别，添加到自定义级别列表
    customLevels.value.push(value);
    // 同时添加到选项列表中
    levelOptions.value.push({ value: value, label: value });
  }
};
// 组件挂载时加载级别选项
onMounted(() => {
  loadLevelOptions();
});

// 添加处理多图上传变化的方法
const handleImagesChange = (urls: string[]) => {
  formData.value.imageList = urls;
  // 保持单图兼容性
  formData.value.image = urls.length > 0 ? urls[0] : "";
};

// 格式化日期函数
const formatDateTime = (date: string | null | undefined) => {
  if (!date) return undefined;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// 处理保存
const handleSave = async () => {
  if (!formRef.value) return;

  try {
    const valid = await formRef.value.validate();
    if (valid) {
      // 构建请求数据
      const requestData = {
        id: formData.value.id,
        standardName: formData.value.standardName?.trim(),
        level: formData.value.level?.trim(),
        standardNumber: formData.value.standardNumber?.trim(),
        responsibleUnit: formData.value.responsibleUnit?.trim(),
        publicationDate: formatDateTime(formData.value.publicationDate),
        implementationDate: formatDateTime(formData.value.implementationDate),
        scope: formData.value.scope?.trim(),
        departmentName: formData.value.departmentName?.trim(),
        draftAuthor: formData.value.draftAuthor?.trim(),
        draftUnit: formData.value.draftUnit?.trim(),
        normativeDocuments: formData.value.normativeDocuments?.trim(),
        imageList: formData.value.imageList ?? [], // 明确空数组
        image: formData.value.imageList?.join(",") || "", // 兼容旧单图逻辑
      };

      const response = await updateStandard(requestData);

      if (response.code === 1 || response.code === 200) {
        ElMessage.success("标准更新成功");
        emit("update", requestData);

        // 如果是自定义级别且成功保存，确保它出现在选项中
        if (
          formData.value.level &&
          !levelOptions.value.some(
            (level) => level.value === formData.value.level
          )
        ) {
          customLevels.value.push(formData.value.level);
          levelOptions.value.push({
            value: formData.value.level,
            label: formData.value.level,
          });
        }
        // 添加页面刷新
      } else {
        ElMessage.error(response.msg || "更新失败");
      }
    }
  } catch (error: any) {
    console.error("更新失败:", error);
    const errorMsg =
      error.response?.data?.msg || error.message || "更新失败，请重试";
    ElMessage.error(errorMsg);
  }
};

// 处理取消
const handleCancel = () => {
  // 重置表单数据
  formData.value = {
    id: 0,
    standardName: "",
    level: "",
    standardNumber: "",
    responsibleUnit: "",
    publicationDate: "",
    implementationDate: "",
    scope: "",
    departmentName: "",
    draftAuthor: "",
    draftUnit: "",
    normativeDocuments: "",
    imageList: [],
    image: "",
    file: "",
    fileName: "",
  };
  // 通知父组件关闭页面
  emit("update", {}); // 使用空对象代替 null
};

// 监听 props 变化，更新表单数据
watch(
  () => props.standardData,
  (newVal) => {
    if (newVal) {
      formData.value = {
        id: newVal.id || 0,
        standardName: newVal.standardName || "",
        level: newVal.level || "",
        standardNumber: newVal.standardNumber || "",
        responsibleUnit: newVal.responsibleUnit || "",
        publicationDate: newVal.publicationDate || "",
        implementationDate: newVal.implementationDate || "",
        scope: newVal.scope || "",
        departmentName: newVal.departmentName || "",
        draftAuthor: newVal.draftAuthor || "",
        draftUnit: newVal.draftUnit || "",
        normativeDocuments: newVal.normativeDocuments || "",
        imageList: newVal.image
          ? newVal.image.split(",").filter((item: string) => item.trim())
          : [],
        image: "",
        file: newVal.file || "",
        fileName: newVal.fileName || "",
      };
      fileDatas.value = {
        standardId: newVal.id,
        fileId: "",
        fileName: "",
        file: "",
      };
      // 如果编辑数据中有自定义级别，确保它出现在选项中
      if (
        newVal.level &&
        !levelOptions.value.some((level) => level.value === newVal.level)
      ) {
        customLevels.value.push(newVal.level);
        levelOptions.value.push({ value: newVal.level, label: newVal.level });
      }
    }
  },
  { immediate: true, deep: true }
);

// 修改 fetchTermsData 函数
const fetchTermsData = async () => {
  if (!props.standardData?.id) {
    console.warn("缺少standardId");
    return;
  }

  try {
    loading.value = true;
    const standardId = props.standardData.id;

    // 添加API调用
    const response = await getTermsByStandardId({
      params: {
        id: standardId,
      },
    });

    if (response?.code === 1) {
      // 更新 termsData
      termsData.value = {
        standardId: standardId,
        terms: response.data || [],
      };
    } else {
      ElMessage.error(response?.msg || "获取术语数据失败");
    }
  } catch (error) {
    console.error("获取术语数据失败:", error);
    ElMessage.error("获取术语数据失败");
  } finally {
    loading.value = false;
  }
};

// 修改监听器，监听 standardId 而不是 id
watch(
  () => props.standardData?.id,
  (newId) => {
    if (newId) {
      fetchTermsData();
      console.log("id changed:", newId);
    }
  },
  { immediate: true }
);

// 导出需要的变量和方法
defineExpose({
  termsData,
  fetchTermsData,
});
</script>

<style scoped>
.action-buttons {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 20px;
}
.resizable-aside {
  position: relative;
  height: 100vh;
  transition: width 0.05s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 1px solid #dcdfe6;
  background-color: #fff;
}

/* 拖拽时禁用过渡效果 */
.resizable-aside.is-dragging {
  transition: none;
}

.resize-handle {
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  background-color: transparent;
  cursor: col-resize;
  z-index: 100;
  transition: background-color 0.2s;
}

.resize-handle:hover,
.resize-handle.is-active {
  background-color: var(--el-color-primary, #409eff);
  opacity: 0.4;
}

/* 拖拽时的全局样式 */
:global(body.is-dragging) {
  user-select: none;
  cursor: col-resize !important;
}

/* 拖拽时的遮罩层 */
.resizable-aside.is-dragging::after {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: transparent;
}

/* 优化滚动条样式 */
.resizable-aside :deep(.el-scrollbar) {
  height: 100%;
}

.resizable-aside :deep(.el-scrollbar__wrap) {
  overflow-x: hidden;
}
</style>
